import { useEffect, useMemo, useRef, useState } from "react";
import useEvent from "react-use-event-hook";

import { useMapColors } from "@ryde/redux";
import {
  decodeRouteLatLngPairs,
  DirectionEnum,
  getDefaultColorObject,
  ILocationInfo,
  IRideRoute,
  isLocationInfoWithValidMapCoordinates,
  latLngTuplesToLineStringFeature,
  Log,
  OsrmApi,
  reverseLatLngTuples,
  shouldReversePlannedRoutePolyline,
  TLatLngTuple,
  TLngLatTuple,
  toLineStringFeature,
} from "@ryde/shared";

import { MIN_LINE_POINTS } from "../route-block-maplibre.constants";
import { IUseMapLibrePlannedRouteResult } from "./use-maplibre-planned-route.models";

interface IUseMapLibrePlannedRouteParams {
  route: IRideRoute;
  showPlannedPath: boolean;
  isEditRoutePolylineAllowed: boolean;
  direction?: DirectionEnum;
}

/**
 * Planned-route geometry for the MapLibre map: decodes the saved polyline, resolves the route colour, and
 * falls back to an OSRM road-following preview (straight line while it loads) when no polyline is saved.
 * Keeps the heavy derivation out of the component, which only renders the returned values.
 */
export const useMapLibrePlannedRoute = ({
  route,
  showPlannedPath,
  direction,
}: IUseMapLibrePlannedRouteParams): IUseMapLibrePlannedRouteResult => {
  const {
    locations,
    routePolyline,
    rideId,
    stationRouteId,
    courseId,
    courseLineColor,
    direction: routeDirection,
  } = route as Required<IRideRoute>;
  const effectiveDirection = direction ?? routeDirection;
  const { getMapColorByRideId } = useMapColors();
  const [osrmFallbackPositions, setOsrmFallbackPositions] = useState<
    TLatLngTuple[] | null
  >(null);
  const latestOsrmRequestIdRef = useRef(0);

  const markerLocations = useMemo(
    () =>
      (locations ?? []).filter(
        isLocationInfoWithValidMapCoordinates,
      ) as Required<ILocationInfo>[],
    [locations],
  );

  const rawPositions = useMemo(
    () => decodeRouteLatLngPairs(routePolyline ?? ""),
    [routePolyline],
  );

  const shouldReverseStoredPolyline = useMemo(
    () =>
      shouldReversePlannedRoutePolyline(effectiveDirection, {
        polylinePositions: rawPositions,
        markerLocations,
      }),
    [effectiveDirection, markerLocations, rawPositions],
  );

  const displayPositions = useMemo(
    () =>
      shouldReverseStoredPolyline
        ? reverseLatLngTuples(rawPositions)
        : rawPositions,
    [rawPositions, shouldReverseStoredPolyline],
  );

  const colorValue = useMemo(() => {
    return rideId
      ? courseLineColor?.trim() || getMapColorByRideId(rideId)
      : getDefaultColorObject({ stationRouteId, courseId }).colorValue;
  }, [courseId, courseLineColor, getMapColorByRideId, rideId, stationRouteId]);

  // Station-only encodings have ~one vertex per stop and draw as crow-flies through buildings.
  const hasDetailedPolyline =
    displayPositions.length >= MIN_LINE_POINTS &&
    displayPositions.length > markerLocations.length;

  const lineFeature = useMemo(() => {
    if (!showPlannedPath || !hasDetailedPolyline) {
      return null;
    }

    return latLngTuplesToLineStringFeature(displayPositions);
  }, [displayPositions, hasDetailedPolyline, showPlannedPath]);

  const fallbackLineFeature = useMemo(() => {
    if (
      lineFeature ||
      !showPlannedPath ||
      markerLocations.length < MIN_LINE_POINTS
    ) {
      return null;
    }

    return toLineStringFeature(
      markerLocations.map(({ longitude, latitude }) => [longitude, latitude]),
    );
  }, [lineFeature, markerLocations, showPlannedPath]);

  // Road-following OSRM preview when there is no detailed polyline (MapLibre twin of Leaflet
  // RoutingMachine). Courses also need this: polyline-edit mode must not leave a crow-flies line.
  // While the request is in flight or if it fails, the straight-line `fallbackLineFeature` stays
  // as the visible path for non-editor routes.
  const shouldFetchOsrmFallback =
    showPlannedPath &&
    !lineFeature &&
    markerLocations.length >= MIN_LINE_POINTS;

  const osrmFallbackCoordsKey = useMemo(
    () =>
      shouldFetchOsrmFallback
        ? markerLocations
            .map(
              ({ longitude, latitude }, index) =>
                `${index}:${longitude},${latitude}`,
            )
            .join(";")
        : "",
    [shouldFetchOsrmFallback, markerLocations],
  );

  const loadOsrmFallbackRoute = useEvent((): void => {
    const coords: TLngLatTuple[] = markerLocations.map(
      ({ longitude, latitude }) => [longitude, latitude],
    );

    // Each request claims the next id and may only publish its result while it is still the latest. A single
    // shared "cancelled" boolean cannot express this: the effect run that starts request N+1 resets the flag
    // to "not cancelled", so a slower request N still passed the check and overwrote the newer geometry with
    // the waypoints the user had already moved away from.
    latestOsrmRequestIdRef.current += 1;
    const requestId = latestOsrmRequestIdRef.current;

    // Master's `getRoutePolyline` (RYDE-7796) returns the encoded geometry directly.
    OsrmApi.getRoutePolyline(coords)
      .then(geometry => {
        if (requestId === latestOsrmRequestIdRef.current && geometry) {
          const decoded = decodeRouteLatLngPairs(geometry);
          setOsrmFallbackPositions(decoded);
        }
      })
      .catch(error => {
        Log.warn("MapLibre OSRM route preview failed", error);
      });
  });

  useEffect(() => {
    if (!osrmFallbackCoordsKey) {
      // Retire the in-flight request too, or its response would repaint the preview we just cleared.
      latestOsrmRequestIdRef.current += 1;
      setOsrmFallbackPositions(null);
      return;
    }

    loadOsrmFallbackRoute();

    return () => {
      // Waypoints changed or the route unmounted: whatever is in flight is now stale.
      latestOsrmRequestIdRef.current += 1;
    };
    // Refetch only when the waypoint set (encoded key) changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [osrmFallbackCoordsKey]);

  const osrmFallbackLineFeature = useMemo(() => {
    if (
      !osrmFallbackPositions ||
      osrmFallbackPositions.length < MIN_LINE_POINTS
    ) {
      return null;
    }

    const feature = latLngTuplesToLineStringFeature(osrmFallbackPositions);
    return feature;
  }, [osrmFallbackPositions]);

  const resolveEditorPositions = (): TLatLngTuple[] => {
    if (hasDetailedPolyline) {
      return rawPositions;
    }

    if (
      osrmFallbackPositions &&
      osrmFallbackPositions.length >= MIN_LINE_POINTS
    ) {
      return osrmFallbackPositions;
    }

    return [];
  };

  const editorPositions = resolveEditorPositions();

  return {
    positions: displayPositions,
    editorPositions,
    markerLocations,
    colorValue,
    pathFeature: lineFeature ?? osrmFallbackLineFeature ?? fallbackLineFeature,
  };
};
